import React, { useState, useEffect } from 'react';
import { Spinner, Button } from 'react-bootstrap';
import { Link, useLocation } from 'react-router-dom';

/**
 * Article History Component
 * Shows list of previously generated articles with modern card design
 */
const ArticleHistory = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState(null);
  const location = useLocation();

  // Determine the correct base path for article links
  const isTenantApp = location.pathname.startsWith('/app');
  const basePath = isTenantApp ? '/app/article-to-media' : '/free';

  useEffect(() => {
    fetchHistory();
  }, [page, isTenantApp]);

  const fetchHistory = async () => {
    try {
      // Use tenant-specific endpoint for authenticated users, public endpoint for free tool
      const endpoint = isTenantApp
        ? `/api/user/history?page=${page}&limit=10`
        : `/api/tools/articles/history?page=${page}&limit=10`;

      const response = await fetch(endpoint, { credentials: 'include' });
      const data = await response.json();
      setArticles(data.articles || []);
      setPagination(data.pagination);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching history:', err);
      setLoading(false);
    }
  };

  const truncateTitle = (title, maxLength = 50) => {
    if (!title) return 'Untitled';
    if (title.length <= maxLength) return title;
    return title.substring(0, maxLength) + '...';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="text-center py-4">
        <Spinner animation="border" size="sm" />
      </div>
    );
  }

  if (articles.length === 0) {
    return null;
  }

  return (
    <div style={{
      background: 'white',
      borderRadius: '16px',
      boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
      overflow: 'hidden'
    }}>
      <div style={{
        padding: '1.25rem 1.5rem',
        borderBottom: '1px solid #f1f5f9',
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem'
      }}>
        <div style={{
          width: '32px',
          height: '32px',
          borderRadius: '8px',
          background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
            <circle cx="12" cy="12" r="10"></circle>
            <polyline points="12 6 12 12 16 14"></polyline>
          </svg>
        </div>
        <h5 style={{ margin: 0, fontWeight: '600', color: '#1a1a2e' }}>Recently Spawned</h5>
      </div>

      <div style={{ padding: '0.5rem' }}>
        {articles.map((article, index) => (
          <Link
            key={article.id}
            to={`${basePath}/${article.slug}`}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '1rem 1.25rem',
              textDecoration: 'none',
              borderRadius: '12px',
              transition: 'background 0.2s',
              marginBottom: index < articles.length - 1 ? '0.25rem' : 0
            }}
            onMouseOver={(e) => e.currentTarget.style.background = '#f8fafc'}
            onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{
                fontWeight: '500',
                color: '#1a1a2e',
                marginBottom: '0.25rem',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}>
                {truncateTitle(article.article_title)}
              </div>
              <div style={{
                fontSize: '0.8rem',
                color: '#94a3b8',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                <span style={{
                  padding: '0.125rem 0.5rem',
                  background: '#f1f5f9',
                  borderRadius: '4px',
                  fontSize: '0.75rem'
                }}>
                  {article.source_domain}
                </span>
                <span>{formatDate(article.completed_at)}</span>
              </div>
            </div>
            <div style={{
              width: '32px',
              height: '32px',
              borderRadius: '8px',
              background: '#f1f5f9',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#64748b',
              flexShrink: 0,
              marginLeft: '1rem'
            }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="5" y1="12" x2="19" y2="12"></line>
                <polyline points="12 5 19 12 12 19"></polyline>
              </svg>
            </div>
          </Link>
        ))}
      </div>

      {pagination && pagination.totalPages > 1 && (
        <div style={{
          padding: '1rem 1.5rem',
          borderTop: '1px solid #f1f5f9',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <span style={{ fontSize: '0.8rem', color: '#94a3b8' }}>
            Page {pagination.page} of {pagination.totalPages}
          </span>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <Button
              variant="outline-secondary"
              size="sm"
              disabled={page === 1}
              onClick={() => setPage(p => p - 1)}
              style={{ borderRadius: '8px', padding: '0.375rem 0.75rem' }}
            >
              Previous
            </Button>
            <Button
              variant="outline-secondary"
              size="sm"
              disabled={page === pagination.totalPages}
              onClick={() => setPage(p => p + 1)}
              style={{ borderRadius: '8px', padding: '0.375rem 0.75rem' }}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ArticleHistory;
